import type {
  StorefrontBuyerSessionResponse,
  StorefrontEncryptedAvailabilityRecord,
  StorefrontEncryptedCatalogRecord,
  StorefrontOrderIntakeKey,
  StorefrontProductImageDescriptor,
} from '@stokmerter/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  downloadBuyerCatalogImage,
  loadBuyerCatalog,
  loadBuyerOrders,
  logoutBuyer,
  submitBuyerOrder,
} from './api';
import { unwrapBuyerCatalogKey } from './buyer-device';
import { BuyerOrderDetailPage } from './buyer-order-detail-page';
import { BuyerOrdersPage, type BuyerOrder, type PageFeedback } from './buyer-orders-page';
import { decryptAvailabilityRecord, decryptCatalogImage, decryptCatalogRecord } from './catalog-crypto';
import { checkoutErrorMessage, checkoutLinePieces, normalizeCheckoutText } from './checkout';
import { CatalogProductCard } from './catalog-product-card';
import { CatalogProductDetail } from './catalog-product-detail';
import {
  primaryImage,
  type CatalogProduct,
  type CatalogViewType,
} from './catalog-types';
import {
  decryptBuyerOrder,
  decryptBuyerOrderDecision,
  decryptBuyerOrderStatus,
  sealStorefrontOrder,
} from './storefront-order-crypto';

type Buyer = NonNullable<StorefrontBuyerSessionResponse['buyer']>;
type CartLine = {
  product: CatalogProduct;
  variantId: string;
  colorName: string;
  seriesQuantity: number;
  extraQuantity: number;
};
type CatalogPanel = 'account' | 'cart';

function productSearchText(product: CatalogProduct): string {
  return [product.payload.productCode, product.payload.productName, product.payload.category,
    ...product.payload.colors.map((color) => color.name)].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
}

function lineKey(productId: string, variantId: string) { return `${productId}:${variantId}`; }

function safeQuantity(value: number): number {
  return Number.isSafeInteger(value) ? Math.min(1_000_000, Math.max(0, value)) : 0;
}

function detailProductCode(pathname: string): string | null {
  const match = pathname.match(/^\/urun\/([^/]+)\/?$/u);
  if (!match?.[1]) return null;
  try { return decodeURIComponent(match[1]); } catch { return null; }
}

function isOrdersPath(pathname: string): boolean {
  return /^\/siparislerim(?:\/[^/]+)?\/?$/u.test(pathname);
}

function buyerOrderIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/siparislerim\/([^/]+)\/?$/u);
  if (!match?.[1]) return null;
  try { return decodeURIComponent(match[1]); } catch { return null; }
}

async function loadImageUrl(input: {
  catalogKey: CryptoKey;
  descriptor: StorefrontProductImageDescriptor;
  recordId: string;
  storefrontId: string;
  signal: AbortSignal;
}): Promise<string> {
  const ciphertext = await downloadBuyerCatalogImage(input.descriptor.imageId, input.signal);
  const plaintext = await decryptCatalogImage({ ...input, ciphertext });
  return URL.createObjectURL(new Blob([plaintext], { type: input.descriptor.mimeType }));
}

function SearchIcon() {
  return <svg aria-hidden="true" viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m15.5 15.5 5 5" /></svg>;
}

function CartIcon() {
  return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 8H6" />
    <circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" /></svg>;
}

export function CatalogPage(props: {
  accentColorToken: string;
  buyer: Buyer;
  displayName: string;
  onLoggedOut: () => void;
  storefrontId: string;
}) {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [catalogKey, setCatalogKey] = useState<CryptoKey | null>(null);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [intakeKey, setIntakeKey] = useState<StorefrontOrderIntakeKey | null>(null);
  const [cart, setCart] = useState<Map<string, CartLine>>(new Map());
  const [orders, setOrders] = useState<BuyerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('Tümü');
  const [viewType, setViewType] = useState<CatalogViewType>('lifestyle');
  const [selected, setSelected] = useState<CatalogProduct | null>(null);
  const [ordersPageOpen, setOrdersPageOpen] = useState(() => isOrdersPath(window.location.pathname));
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(() =>
    buyerOrderIdFromPath(window.location.pathname));
  const [panel, setPanel] = useState<CatalogPanel | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(props.buyer.profile.shippingAddress);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingIntake, setCheckingIntake] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panelFeedback, setPanelFeedback] = useState<PageFeedback | null>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closePopovers = (event: PointerEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
      if (!searchRef.current?.contains(event.target as Node)) setSearchOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAccountMenuOpen(false);
        setSearchOpen(false);
      }
    };
    document.addEventListener('pointerdown', closePopovers);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closePopovers);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (!props.buyer.catalogKeyEnvelope) {
      setError('Bu cihazın katalog erişimi firma onayı bekliyor.');
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const createdUrls: string[] = [];
    void (async () => {
      try {
        const key = await unwrapBuyerCatalogKey({ storefrontId: props.storefrontId,
          buyerId: props.buyer.buyerId, deviceId: props.buyer.deviceId,
          envelope: props.buyer.catalogKeyEnvelope! });
        const records: StorefrontEncryptedCatalogRecord[] = [];
        const availabilityRecords: StorefrontEncryptedAvailabilityRecord[] = [];
        let cursor: string | undefined;
        let revision: number | null = null;
        let orderKey: StorefrontOrderIntakeKey | null = null;
        do {
          const page = await loadBuyerCatalog(cursor, controller.signal);
          revision ??= page.catalogRevision;
          if (revision !== page.catalogRevision) throw new Error('CATALOG_REVISION_CHANGED');
          orderKey ??= page.orderIntakeKey;
          records.push(...page.records);
          availabilityRecords.push(...page.availabilityRecords);
          cursor = page.hasMore && page.nextCursor ? page.nextCursor : undefined;
        } while (cursor);
        const availability = new Map<string, 'in_stock' | 'out_of_stock'>();
        for (const record of availabilityRecords) {
          const value = await decryptAvailabilityRecord({ catalogKey: key, record,
            storefrontId: props.storefrontId });
          const identity = lineKey(record.productId, record.variantId);
          availability.set(identity,
            availability.get(identity) === 'in_stock' || value === 'in_stock' ? 'in_stock' : 'out_of_stock');
        }
        const decrypted = await Promise.all(records.map(async (record) => {
          const originalPayload = await decryptCatalogRecord({ catalogKey: key, record,
            storefrontId: props.storefrontId });
          const payload = { ...originalPayload, colors: originalPayload.colors.map((color) => ({
            ...color, availability: availability.get(lineKey(record.recordId, color.variantId)) ?? color.availability,
          })) };
          const images = await Promise.all(payload.images.map(async (descriptor) => {
            try {
              const url = await loadImageUrl({ catalogKey: key, descriptor, recordId: record.recordId,
                storefrontId: props.storefrontId, signal: controller.signal });
              createdUrls.push(url);
              return { url, role: descriptor.role ?? 'lifestyle' };
            } catch { return null; }
          }));
          return { recordId: record.recordId, recordVersion: record.recordVersion,
            payload, images: images.filter((image): image is NonNullable<typeof image> => Boolean(image)) };
        }));
        setCatalogKey(key);
        setCatalogRevision(revision ?? 0);
        setIntakeKey(orderKey);
        setProducts(decrypted);
        setError(null);
      } catch (loadError) {
        if (!controller.signal.aborted) setError(loadError instanceof Error &&
          loadError.message === 'CATALOG_DEVICE_KEY_MISSING'
          ? 'Bu oturuma ait güvenli tarayıcı anahtarı bulunamadı. Yeniden giriş yapın.'
          : 'Ürün kataloğu güvenli biçimde açılamadı.');
      } finally { if (!controller.signal.aborted) setLoading(false); }
    })();
    return () => { controller.abort(); for (const url of createdUrls) URL.revokeObjectURL(url); };
  }, [props.buyer, props.storefrontId]);

  const categories = useMemo(() => ['Tümü', ...Array.from(new Set(products.map((item) =>
    item.payload.category).filter((item): item is string => Boolean(item)))).sort((left, right) =>
    left.localeCompare(right, 'tr-TR'))], [products]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('tr-TR');
    return products.filter((product) => (category === 'Tümü' || product.payload.category === category) &&
      (!needle || productSearchText(product).includes(needle)));
  }, [category, products, query]);
  const cartLines = [...cart.values()];
  const cartSummary = cartLines.reduce((summary, line) => ({
    lines: summary.lines + 1,
    series: summary.series + line.seriesQuantity,
    extras: summary.extras + line.extraQuantity,
    pieces: summary.pieces + checkoutLinePieces({
      seriesCount: line.product.payload.seriesCount,
      seriesQuantity: line.seriesQuantity,
      extraQuantity: line.extraQuantity,
    }),
  }), { lines: 0, series: 0, extras: 0, pieces: 0 });
  const selectedQuantities = selected ? Object.fromEntries(selected.payload.colors.map((color) => [
    color.variantId,
    cart.get(lineKey(selected.recordId, color.variantId))?.seriesQuantity ?? 0,
  ])) : {};

  useEffect(() => {
    const syncRoute = () => {
      if (isOrdersPath(window.location.pathname)) {
        setOrdersPageOpen(true);
        setSelectedOrderId(buyerOrderIdFromPath(window.location.pathname));
        setSelected(null);
        setPanel(null);
        return;
      }
      setOrdersPageOpen(false);
      setSelectedOrderId(null);
      const code = detailProductCode(window.location.pathname);
      if (!code) {
        setSelected(null);
        return;
      }
      setSelected(products.find((product) => product.payload.productCode === code) ?? null);
    };
    syncRoute();
    window.addEventListener('popstate', syncRoute);
    return () => window.removeEventListener('popstate', syncRoute);
  }, [products]);

  function openDetail(product: CatalogProduct) {
    window.history.pushState({ storefrontProduct: product.recordId }, '',
      `/urun/${encodeURIComponent(product.payload.productCode)}`);
    setOrdersPageOpen(false);
    setSelectedOrderId(null);
    setSelected(product);
  }
  function closeDetail() {
    if (window.history.state?.storefrontProduct) window.history.back();
    else window.history.replaceState(null, '', '/');
    setSelected(null);
  }
  function updateLine(line: CartLine, seriesQuantity: number, extraQuantity: number) {
    const next = new Map(cart);
    const identity = lineKey(line.product.recordId, line.variantId);
    const safeSeriesQuantity = safeQuantity(seriesQuantity);
    const safeExtraQuantity = safeQuantity(extraQuantity);
    if (safeSeriesQuantity <= 0 && safeExtraQuantity <= 0) next.delete(identity);
    else next.set(identity, { ...line, seriesQuantity: safeSeriesQuantity,
      extraQuantity: safeExtraQuantity });
    setCart(next);
    setPanelFeedback(null);
  }
  function applyProductQuantities(product: CatalogProduct, quantities: Readonly<Record<string, number>>) {
    const next = new Map(cart);
    for (const color of product.payload.colors) {
      const identity = lineKey(product.recordId, color.variantId);
      const current = next.get(identity);
      const seriesQuantity = safeQuantity(quantities[color.variantId] ?? 0);
      if (seriesQuantity <= 0 && (current?.extraQuantity ?? 0) <= 0) next.delete(identity);
      else next.set(identity, {
        product,
        variantId: color.variantId,
        colorName: color.name,
        seriesQuantity,
        extraQuantity: current?.extraQuantity ?? 0,
      });
    }
    setCart(next);
    setNotice(`${product.payload.productName} siparişe eklendi.`);
    closeDetail();
  }
  const loadOrderHistory = useCallback(async (): Promise<void> => {
    const response = await loadBuyerOrders();
    const detailed = await Promise.all(response.orders.map(async (order) => ({
      ...order,
      orderDetail: catalogKey
        ? await decryptBuyerOrder({ catalogKey, order }).catch(() => null)
        : null,
      decisionDetail: catalogKey
        ? await decryptBuyerOrderDecision({ catalogKey, order }).catch(() => null)
        : null,
      statusDetail: catalogKey
        ? await decryptBuyerOrderStatus({ catalogKey, order }).catch(() => null)
        : null,
    })));
    setOrders(detailed);
  }, [catalogKey]);
  const refreshOrderHistory = useCallback(async (): Promise<void> => {
    setOrdersLoading(true);
    try {
      await loadOrderHistory();
    } catch {
      setPanelFeedback({ tone: 'error', text: 'Siparişler şu anda yüklenemedi. Tekrar deneyin.' });
    } finally { setOrdersLoading(false); }
  }, [loadOrderHistory]);
  useEffect(() => {
    if (ordersPageOpen && catalogKey) void refreshOrderHistory();
  }, [catalogKey, ordersPageOpen, refreshOrderHistory]);
  function openOrders() {
    if (!/^\/siparislerim\/?$/u.test(window.location.pathname)) {
      window.history.pushState({ storefrontOrders: true }, '', '/siparislerim');
    }
    setOrdersPageOpen(true);
    setSelectedOrderId(null);
    setSelected(null);
    setPanel(null);
    setNotice(null);
    setPanelFeedback(null);
  }
  function closeOrders() {
    if (window.history.state?.storefrontOrders) window.history.back();
    else window.history.replaceState(null, '', '/');
    setOrdersPageOpen(false);
    setSelectedOrderId(null);
    setPanelFeedback(null);
  }
  function openBuyerOrder(orderId: string) {
    window.history.pushState({ storefrontOrder: orderId }, '',
      `/siparislerim/${encodeURIComponent(orderId)}`);
    setOrdersPageOpen(true);
    setSelectedOrderId(orderId);
    setPanelFeedback(null);
  }
  function closeBuyerOrder() {
    if (window.history.state?.storefrontOrder) window.history.back();
    else window.history.replaceState({ storefrontOrders: true }, '', '/siparislerim');
    setOrdersPageOpen(true);
    setSelectedOrderId(null);
    setPanelFeedback(null);
  }
  function chooseCategory(item: string) {
    setCategory(item);
    window.history.replaceState(null, '', '/');
    setOrdersPageOpen(false);
    setSelectedOrderId(null);
    setSelected(null);
  }
  function updateQuery(value: string) {
    setQuery(value);
    if (selected || ordersPageOpen) {
      window.history.replaceState(null, '', '/');
      setOrdersPageOpen(false);
      setSelectedOrderId(null);
      setSelected(null);
    }
  }
  async function checkOrderChannel() {
    setCheckingIntake(true);
    setPanelFeedback(null);
    try {
      const response = await loadBuyerCatalog();
      setIntakeKey(response.orderIntakeKey);
      setPanelFeedback(response.orderIntakeKey
        ? { tone: 'success', text: 'Sipariş kanalı hazır. Siparişinizi gönderebilirsiniz.' }
        : { tone: 'info', text: 'Sipariş kanalı henüz hazır değil. Firma yetkilisi paneli yeniledikten sonra tekrar kontrol edin.' });
    } catch {
      setPanelFeedback({ tone: 'error', text: 'Sipariş kanalı kontrol edilemedi. Bağlantınızı kontrol edin.' });
    } finally { setCheckingIntake(false); }
  }
  async function placeOrder() {
    setPanelFeedback(null);
    if (!intakeKey) {
      setPanelFeedback({ tone: 'info', text: 'Firma sipariş kanalını henüz etkinleştirmedi. Firma paneli yenilendikten sonra tekrar kontrol edin.' });
      return;
    }
    if (!catalogKey || !props.buyer.catalogKeyEnvelope) {
      setPanelFeedback({ tone: 'error', text: 'Güvenli katalog anahtarı hazır değil. Sayfayı yenileyip tekrar deneyin.' });
      return;
    }
    if (!shippingAddress.trim()) {
      setPanelFeedback({ tone: 'error', text: 'Siparişi göndermek için teslimat adresini girin.' });
      return;
    }
    if (cartLines.length === 0) {
      setPanelFeedback({ tone: 'error', text: 'Sipariş göndermek için sepete en az bir ürün ekleyin.' });
      return;
    }
    setSubmitting(true);
    setNotice(null);
    try {
      const submittedAt = new Date().toISOString();
      const request = await sealStorefrontOrder({
        buyerId: props.buyer.buyerId,
        catalogKey,
        catalogKeyVersion: props.buyer.catalogKeyEnvelope.keyVersion,
        storefrontId: props.storefrontId, catalogRevision, intakeKey,
        payload: {
          version: 1,
          items: cartLines.map((line) => ({
            productId: line.product.recordId, variantId: line.variantId,
            productCode: line.product.payload.productCode, productName: line.product.payload.productName,
            colorName: line.colorName, seriesCount: line.product.payload.seriesCount,
            seriesQuantity: line.seriesQuantity, extraQuantity: line.extraQuantity,
            displayedPriceTlMinor: props.buyer.currency === 'TRY' ? line.product.payload.priceTlMinor : null,
            displayedPriceUsdMinor: props.buyer.currency === 'USD' ? line.product.payload.priceUsdMinor : null,
            catalogRecordVersion: line.product.recordVersion,
          })),
          note: normalizeCheckoutText(note) || null,
          shippingAddress: normalizeCheckoutText(shippingAddress),
          submittedAt,
        },
      });
      await submitBuyerOrder(request);
      setCart(new Map());
      setNote('');
      setPanelFeedback({ tone: 'success', text: 'Siparişiniz firmaya iletildi. Firma onayladığında durumu burada göreceksiniz.' });
      window.history.pushState({ storefrontOrder: request.orderId }, '',
        `/siparislerim/${encodeURIComponent(request.orderId)}`);
      setOrdersPageOpen(true);
      setSelectedOrderId(request.orderId);
      setSelected(null);
      setPanel(null);
      try {
        await loadOrderHistory();
      } catch { /* Successful submission remains visible even if history refresh fails. */ }
    } catch (submitError) {
      setPanelFeedback({ tone: 'error', text: checkoutErrorMessage(submitError) });
    }
    finally { setSubmitting(false); }
  }

  return <main className="catalog-shell" data-accent={props.accentColorToken}>
    <header className="catalog-header">
      <a className="catalog-brand" href="/">{props.displayName}</a>
      <nav className="header-categories" aria-label="Ürün kategorileri">{categories.map((item) =>
        <button aria-current={category === item ? 'page' : undefined}
          className={category === item ? 'active' : ''} key={item}
          onClick={() => chooseCategory(item)} type="button">{item}</button>)}</nav>
      <div className="catalog-tools">
        <div className={`header-search${searchOpen ? ' open' : ''}`} ref={searchRef}>
          <button aria-expanded={searchOpen} aria-label="Ürün ara" className="header-icon-button"
            onClick={() => setSearchOpen((current) => !current)} type="button"><SearchIcon /></button>
          {searchOpen ? <label className="header-search-popover"><span>Ürün ara</span><input autoFocus
            onChange={(event) => updateQuery(event.currentTarget.value)}
            placeholder="Ürün kodu, adı veya renk ara" type="search" value={query} /></label> : null}
        </div>
        <button aria-label={`Sepetim, ${cartSummary.lines} ürün`} className="header-icon-button cart-icon-button"
          onClick={() => { setPanel('cart'); setPanelFeedback(null); }} type="button"><CartIcon />
          <span>{cartSummary.lines}</span></button>
        <div className="profile-menu" ref={accountMenuRef}>
          <button aria-expanded={accountMenuOpen} aria-haspopup="menu" className="profile-trigger"
            onClick={() => setAccountMenuOpen((current) => !current)} type="button">
            <span>{props.buyer.username}</span><i aria-hidden="true">⌄</i></button>
          {accountMenuOpen ? <div className="profile-dropdown" role="menu">
            <button onClick={() => { setPanel('account'); setPanelFeedback(null); setAccountMenuOpen(false); }}
              role="menuitem" type="button">Hesabım</button>
            <button onClick={() => { setAccountMenuOpen(false); openOrders(); }}
              role="menuitem" type="button">Siparişlerim</button>
            <button className="logout-menu-item" onClick={() => void logoutBuyer().finally(props.onLoggedOut)}
              role="menuitem" type="button">Çıkış yap</button>
          </div> : null}
        </div>
        <div className="view-toggle header-view-toggle" aria-label="Katalog görsel tipi" role="group">
          <button className={viewType === 'lifestyle' ? 'active' : ''}
            onClick={() => setViewType('lifestyle')} type="button">Lifestyle</button>
          <button className={viewType === 'flat' ? 'active' : ''}
            onClick={() => setViewType('flat')} type="button">Flat</button>
        </div>
      </div>
    </header>
    {ordersPageOpen ? selectedOrderId ? <BuyerOrderDetailPage
      feedback={panelFeedback}
      loading={ordersLoading}
      onBack={closeBuyerOrder}
      onRefresh={() => { setPanelFeedback(null); void refreshOrderHistory(); }}
      order={orders.find((order) => order.orderId === selectedOrderId) ?? null}
      products={products}
    /> : <BuyerOrdersPage
      feedback={panelFeedback}
      loading={ordersLoading}
      onBack={closeOrders}
      onOpenOrder={openBuyerOrder}
      onRefresh={() => { setPanelFeedback(null); void refreshOrderHistory(); }}
      orders={orders}
    /> : selected ? <CatalogProductDetail
      currency={props.buyer.currency}
      initialQuantities={selectedQuantities}
      key={selected.recordId}
      onApply={(quantities) => applyProductQuantities(selected, quantities)}
      onBack={closeDetail}
      product={selected}
      viewType={viewType}
    /> : <section className="catalog-content">
      {notice ? <div className="catalog-notice">{notice}</div> : null}
      {loading ? <div className="catalog-state">Ürünler güvenli hesabınızda açılıyor…</div> : null}
      {error ? <div className="catalog-state error">{error}</div> : null}
      {!loading && !error && filtered.length === 0 ? <div className="catalog-state">Aramanıza uygun ürün bulunamadı.</div> : null}
      <div className="product-grid">{filtered.map((product) => <CatalogProductCard
        currency={props.buyer.currency}
        key={product.recordId}
        onOpen={openDetail}
        product={product}
        viewType={viewType}
      />)}</div>
    </section>}

    {panel ? <div className="side-panel-backdrop" onMouseDown={(event) => {
      if (event.currentTarget === event.target) setPanel(null);
    }} role="presentation"><aside aria-modal="true" className="side-panel" role="dialog">
      <div className="side-panel-header"><div><p>{props.displayName}</p><h2>{panel === 'cart' ? 'Sepetim' : 'Hesabım'}</h2></div>
        <button aria-label="Paneli kapat" onClick={() => setPanel(null)} type="button">×</button></div>
      {panelFeedback ? <div aria-live="polite" className={`panel-feedback ${panelFeedback.tone}`}
        role={panelFeedback.tone === 'error' ? 'alert' : 'status'}>{panelFeedback.text}</div> : null}
      {panel === 'account' ? <div className="account-profile"><div className="account-identity">
        <div aria-hidden="true">{props.buyer.profile.companyName.slice(0, 1).toLocaleUpperCase('tr-TR')}</div>
        <span><b>{props.buyer.profile.companyName}</b><small>@{props.buyer.username}</small></span></div>
        <dl><div><dt>Yetkili</dt><dd>{props.buyer.profile.contactName}</dd></div>
          <div><dt>E-posta</dt><dd>{props.buyer.profile.email}</dd></div>
          <div><dt>Telefon</dt><dd>{props.buyer.profile.phone}</dd></div>
          <div><dt>Fiyatlandırma</dt><dd>{props.buyer.currency === 'TRY' ? 'Türk lirası (TL)' : 'Amerikan doları (USD)'}</dd></div>
          {props.buyer.profile.taxNumber ? <div><dt>Vergi</dt><dd>{props.buyer.profile.taxNumber}{props.buyer.profile.taxOffice ? ` · ${props.buyer.profile.taxOffice}` : ''}</dd></div> : null}
          <div><dt>Teslimat adresi</dt><dd>{props.buyer.profile.shippingAddress}</dd></div>
          <div><dt>Fatura adresi</dt><dd>{props.buyer.profile.billingAddress}</dd></div></dl></div>
        : <><div className="cart-lines">{cartLines.length === 0 ? <div className="empty-panel">Sepetiniz boş.</div>
        : cartLines.map((line) => <div className="cart-line" key={lineKey(line.product.recordId, line.variantId)}>
          <div className="cart-product">{primaryImage(line.product, viewType)
            ? <img alt="" src={primaryImage(line.product, viewType)!} /> : <span className="cart-image-placeholder" />}
            <div><b>{line.product.payload.productName}</b><span>{line.colorName} · {line.product.payload.productCode}</span>
              <small>{checkoutLinePieces({ seriesCount: line.product.payload.seriesCount,
                seriesQuantity: line.seriesQuantity, extraQuantity: line.extraQuantity })} parça</small></div></div>
          <div className="cart-quantities"><label>Seri<input min="0" onChange={(event) => updateLine(line, Number(event.currentTarget.value), line.extraQuantity)}
            type="number" value={line.seriesQuantity} /></label>
            <label>Ek adet<input min="0" onChange={(event) => updateLine(line, line.seriesQuantity, Number(event.currentTarget.value))}
              type="number" value={line.extraQuantity} /></label></div>
          <button aria-label={`${line.product.payload.productName} ürününü sepetten sil`}
            onClick={() => updateLine(line, 0, 0)} type="button">Sil</button>
        </div>)}</div>
        {cartLines.length > 0 ? <><div className="cart-summary"><div><small>Ürün</small><b>{cartSummary.lines}</b></div>
          <div><small>Seri</small><b>{cartSummary.series}</b></div><div><small>Ek adet</small><b>{cartSummary.extras}</b></div>
          <div><small>Toplam parça</small><b>{cartSummary.pieces}</b></div></div>
          <div className="checkout-form">{!intakeKey ? <div className="order-channel-notice"><div><b>Sipariş kanalı hazırlanıyor</b>
            <span>Firma yetkilisi paneli yeniledikten sonra yeniden kontrol edin.</span></div>
            <button disabled={checkingIntake} onClick={() => void checkOrderChannel()} type="button">
              {checkingIntake ? 'Kontrol ediliyor…' : 'Tekrar kontrol et'}</button></div> : null}
            <label>Teslimat adresi<textarea onChange={(event) => { setShippingAddress(event.currentTarget.value); setPanelFeedback(null); }}
              rows={3} value={shippingAddress} /></label><label>Sipariş notu <span>(isteğe bağlı)</span><textarea
                onChange={(event) => { setNote(event.currentTarget.value); setPanelFeedback(null); }} rows={2} value={note} /></label>
            <button disabled={submitting || !intakeKey} onClick={() => void placeOrder()} type="button">{submitting
              ? 'Gönderiliyor…' : !intakeKey ? 'Sipariş kanalı hazırlanıyor' : 'Siparişi firmaya gönder'}</button></div></> : null}</>}
    </aside></div> : null}
  </main>;
}
